import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AUTH_BG = "https://images.pexels.com/photos/11093512/pexels-photo-11093512.jpeg";

export default function LoginPage() {
  const { login, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);

  useEffect(() => {
    if (user) navigate("/panel", { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverified(false);
    try {
      await login(email, password);
      toast.success("Hoş geldiniz!");
      navigate("/panel", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      toast.error(msg);
      if (err?.response?.status === 403 && msg.includes("doğrulanmadı")) {
        setUnverified(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-white relative" data-testid="login-page">
      <div className="absolute top-6 right-6 z-20">
        <LanguageToggle />
      </div>
      <div className="hidden lg:block lg:col-span-3 relative auth-noise">
        <img src={AUTH_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A1128]/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Wordmark dark />
          <div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
              {t("Tüm firmalarınızın finansı,")}<br />
              <span className="text-blue-400">{t("tek platformda.")}</span>
            </h1>
          </div>
          <p className="text-slate-400 text-sm">{t("© 2026 TEMHA — Çok firmalı finans yönetim platformu")}</p>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-8 sm:p-12 lg:p-16">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-10">
            <Wordmark />
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128]" data-testid="login-heading">
            {t("Giriş Yap")}
          </h2>
          <p className="mt-2 text-sm text-slate-500">{t("Firma hesabınıza erişmek için giriş yapın.")}</p>

          {unverified && (
            <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 flex gap-3" data-testid="unverified-banner">
              <MailCheck className="h-5 w-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                {t("E-postanız doğrulanmadı.")}{" "}
                <Link to="/verify-email" className="font-medium underline" data-testid="resend-link">
                  {t("Yeni doğrulama bağlantısı isteyin")}
                </Link>
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">{t("E-posta")}</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@firma.com"
                data-testid="login-email-input"
                className="focus-visible:ring-blue-600"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("Şifre")}</Label>
                <Link to="/forgot-password" tabIndex={-1}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                  data-testid="forgot-password-link">
                  {t("Şifremi unuttum")}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="focus-visible:ring-blue-600"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200 hover:-translate-y-[1px]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Giriş Yap")}
            </Button>
          </form>

          <p className="mt-8 text-sm text-slate-500">
            {t("Firmanızı TEMHA'ya taşımak mı istiyorsunuz?")}{" "}
            <Link to="/register-company" className="font-medium text-blue-600 hover:text-blue-700" data-testid="register-company-link">
              {t("Firma kaydı oluşturun")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
